(() => {
  if (customElements.get('afz-shopping-help')) return;
  class AFZShoppingHelp extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.launcher = this.querySelector('.afz-help__launcher');
      this.panel = this.querySelector('.afz-help__panel');
      this.conversation = this.querySelector('.afz-help__conversation');
      this.body = this.querySelector('.afz-help__body');
      this.templates = new Map([...this.querySelectorAll('template[data-question]')].map(t => [t.dataset.question, t]));
      const product = this.dataset.featherlight === 'true';
      this.popular = product ? ['fit', 'returns', 'delivery', 'warmth'] : ['delivery', 'returns', 'fit', 'shipping'];
      if (this.templates.has('offer')) this.popular = ['offer', ...this.popular.slice(0, 3)];
      this.currentKey = this.currentKey || null;
      this.renderQuestions();
      this.launcher.addEventListener('click', () => this.open(), { signal });
      this.querySelector('.afz-help__close').addEventListener('click', () => this.close(true), { signal });
      this.querySelector('.afz-help__continue').addEventListener('click', () => this.close(true), { signal });
      this.querySelector('.afz-help__more').addEventListener('click', event => {
        const button = event.currentTarget;
        const list = this.querySelector('#afz-help-more');
        list.hidden = !list.hidden;
        button.setAttribute('aria-expanded', String(!list.hidden));
        button.firstChild.textContent = list.hidden ? 'Ask something else ' : 'Fewer questions ';
        button.querySelector('span').textContent = list.hidden ? '+' : '−';
      }, { signal });
      this.addEventListener('click', event => {
        const target = event.target.closest('[data-open-size]');
        if (target) this.openSizeChart();
        if (event.target.closest('a')) this.close(false);
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.hasAttribute('data-open')) { event.preventDefault(); this.close(true); }
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (this.hasAttribute('data-open') && !this.contains(event.target)) this.close(false);
      }, { signal });
      document.addEventListener('focusin', event => {
        if (this.hasAttribute('data-open') && !this.contains(event.target)) this.close(false);
      }, { signal });
      this.updatePosition = this.updatePosition.bind(this);
      this.queuePosition = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.updatePosition(); });
      };
      window.addEventListener('resize', this.queuePosition, { signal, passive: true });
      window.visualViewport?.addEventListener('resize', this.queuePosition, { signal, passive: true });
      document.addEventListener('scroll', this.queuePosition, { signal, passive: true });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.queuePosition();
      });
      this.observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'data-stuck', 'class', 'aria-hidden'], childList: true });
      this.updatePosition();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.ready = false;
    }
    questionButton(key) {
      const template = this.templates.get(key);
      if (!template) return document.createDocumentFragment();
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'afz-help__question';
      button.dataset.helpQuestion = key;
      const label = document.createElement('span');
      label.textContent = template.dataset.title;
      button.append(label);
      button.addEventListener('click', () => this.showAnswer(key));
      return button;
    }
    open() {
      this.returnFocus = document.activeElement;
      this.setAttribute('data-open', '');
      this.panel.hidden = false;
      this.launcher.setAttribute('aria-expanded', 'true');
      ([...this.querySelectorAll('.afz-help__reply')].at(-1) || this.querySelector('.afz-help__questions button'))?.focus({ preventScroll: true });
    }
    close(restore) {
      this.removeAttribute('data-open');
      this.panel.hidden = true;
      this.launcher.setAttribute('aria-expanded', 'false');
      if (restore && !this.hidden) (this.returnFocus?.isConnected ? this.returnFocus : this.launcher).focus({ preventScroll: true });
    }
    renderQuestions() {
      const template = this.templates.get(this.currentKey);
      const related = template ? template.dataset.related.split(',') : this.popular;
      const list = this.querySelector('.afz-help__questions');
      const more = this.querySelector('#afz-help-more');
      list.replaceChildren(...related.map(key => this.questionButton(key)));
      more.replaceChildren(...[...this.templates.keys()].filter(key => key !== this.currentKey && !related.includes(key)).map(key => this.questionButton(key)));
      more.hidden = true;
      const toggle = this.querySelector('.afz-help__more');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.firstChild.textContent = 'Ask something else ';
      toggle.querySelector('span').textContent = '+';
      this.querySelector('.afz-help__prompt').textContent = template ? 'What else would help?' : 'Tap a question to start';
      this.querySelector('.afz-help__continue').hidden = !template;
    }
    showAnswer(key) {
      const template = this.templates.get(key);
      if (!template) return;
      this.currentKey = key;
      const question = document.createElement('li');
      question.className = 'afz-help__message afz-help__message--user';
      const label = document.createElement('span');
      label.className = 'afz-help__sender'; label.textContent = 'You';
      const bubble = document.createElement('div');
      bubble.className = 'afz-help__bubble'; bubble.textContent = template.dataset.title;
      question.append(label, bubble);
      const reply = document.createElement('li');
      reply.className = 'afz-help__message afz-help__message--assistant afz-help__reply';
      reply.tabIndex = -1; reply.setAttribute('aria-label', 'AFZ reply');
      const sender = document.createElement('span'); sender.className = 'afz-help__sender'; sender.textContent = 'AFZ';
      const answer = document.createElement('div'); answer.className = 'afz-help__bubble';
      answer.append(template.content.cloneNode(true)); reply.append(sender, answer);
      this.conversation.append(question, reply);
      this.renderQuestions();
      reply.focus({ preventScroll: true });
      this.body.scrollTop += question.getBoundingClientRect().top - this.body.getBoundingClientRect().top - 18;
    }
    openSizeChart() {
      const chart = document.querySelector('.afz-product-notes .afz-fl800-size');
      const details = chart?.closest('details');
      if (!details) { window.location.assign('/pages/size-and-fit'); return; }
      this.close(false); details.open = true;
      const summary = details.querySelector('summary');
      summary.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      summary.focus({ preventScroll: true });
    }
    updatePosition() {
      const mobile = window.matchMedia('(max-width:749px)').matches;
      const height = window.innerHeight;
      let bottom = mobile ? 16 : 24;
      const bar = document.querySelector('sticky-add-to-cart [data-stuck="true"]') || (mobile ? document.querySelector('#afz-cart-page .cart-summary') : null);
      if (bar) { const rect = bar.getBoundingClientRect(); bottom = Math.max(bottom, bar.offsetHeight + (parseFloat(getComputedStyle(bar).bottom) || 0) + 12, rect.top < height && rect.bottom > 0 ? height - rect.top + 12 : 0); }
      const preview = document.querySelector('#PBarNextFrame');
      if (preview) { const rect = preview.getBoundingClientRect(); if (rect.top < height && rect.bottom >= height - 4) bottom = Math.max(bottom, Math.min(rect.height, 64) + 12); }
      const header = document.querySelector('header-component');
      const headerBottom = header?.getBoundingClientRect().bottom || 56;
      this.style.setProperty('--help-bottom', `${Math.round(bottom)}px`);
      this.style.setProperty('--help-top', `${Math.max(20, Math.min(120, headerBottom + 16))}px`);
      const otherOpen = [...document.querySelectorAll('dialog[open],theme-drawer[open],#Details-menu-drawer-container[open]')].some(el => !this.contains(el));
      const keyboardOpen = window.visualViewport && window.innerHeight - window.visualViewport.height > 150;
      if (otherOpen || keyboardOpen) { this.close(false); this.hidden = true; } else { this.hidden = false; }
    }
  }
  customElements.define('afz-shopping-help', AFZShoppingHelp);
})();

